import {
	type AppId,
	appIdToAppInfoMap,
	smartlinkExtensionKeyToAppIdMap,
} from '../rovo-third-party-apps-types';
import { type ConnectorApp } from '../get-connected-data-sources';
import { SMART_CARD_EXTERNAL_AUTH_EVENT } from '@atlaskit/link-provider/provider';

/**
 * Resolves the outbound auth URL and connector type for a given app from a list of connector apps.
 *
 * Priority order:
 * 1. Full connector type (e.g. "drive-connector")
 * 2. SmartLink connector type (e.g. "smartlinks-connector-drive")
 * 3. Fallback: any unauthed connector whose type includes the app ID
 *
 * For OneDrive, retries with "sharepoint" if no URL is found (they share the same auth URL).
 */
export const getAuthUrlFromConnectorDetails = (
	connectorDataDetails: ConnectorApp[],
	targetedApp: string,
): { authUrl: string | undefined; connectorType: string | null } => {
	const appInfo = appIdToAppInfoMap[targetedApp as AppId];
	const fullType = appInfo.fullConnectorType ?? `${targetedApp}-connector`;
	const smartlinkType = appInfo.smartlinkConnectorType ?? `smartlinks-connector-${targetedApp}`;

	const fullMatch = connectorDataDetails.find((entry) => entry.type === fullType);
	const smartlinkMatch = connectorDataDetails.find((entry) => entry.type === smartlinkType);
	const fallbackMatch = connectorDataDetails.find(
		(entry) => entry.type.includes(targetedApp) && entry.isUserOAuthed === false,
	); // search for the app ID in any unconnected types

	let connectorType: string | null = null;
	let authUrl: string | undefined;

	if (fullMatch) {
		connectorType = 'full-connector';
		authUrl = fullMatch.outboundAuthUrl;
	} else if (smartlinkMatch) {
		connectorType = 'smartlink-connector';
		authUrl = smartlinkMatch.outboundAuthUrl;
	} else if (fallbackMatch) {
		authUrl = fallbackMatch.outboundAuthUrl;
		// Determine connectorType based on naming convention
		if (fallbackMatch.type.endsWith('-connector')) {
			connectorType = 'full-connector';
		} else if (fallbackMatch.type.startsWith('smartlinks-connector-')) {
			connectorType = 'smartlink-connector';
		}
	}

	// OneDrive and SharePoint share the same auth URL, so if onedrive yielded no
	// result, retry the lookup using "sharepoint" as a backup in case the TAP trait missed it.
	if (!authUrl && targetedApp === 'onedrive') {
		return getAuthUrlFromConnectorDetails(connectorDataDetails, 'sharepoint');
	}

	return { authUrl, connectorType };
};

export const getExtensionKeysForAppId = (appId: string): string[] => {
	const keys: string[] = [];
	for (const [extensionKey, mappedAppId] of Object.entries(smartlinkExtensionKeyToAppIdMap)) {
		const matches = Array.isArray(mappedAppId)
			? mappedAppId.includes(appId as AppId)
			: mappedAppId === appId;
		if (matches) {
			keys.push(extensionKey);
		}
	}
	return keys;
};

/**
 * Dispatches a window event so any SmartCardProvider on the page can re-resolve
 * unauthorized cards that match the newly authed app.
 */
export const dispatchSmartLinkExternalAuthEvent = (targetedApp: string): void => {
	if (!globalThis.document) {
		return;
	}
	const extensionKeys = getExtensionKeysForAppId(targetedApp);
	if (extensionKeys.length === 0) {
		return;
	}
	globalThis.dispatchEvent(
		new CustomEvent(SMART_CARD_EXTERNAL_AUTH_EVENT, {
			detail: { extensionKeys },
		}),
	);
};
